import React from 'react';
import { ArrowRight, ExternalLink, Play } from 'lucide-react';
import { Link } from 'react-router-dom';

const WorkWithMe = () => {
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);
  const [isKingFoxVideoPlaying, setIsKingFoxVideoPlaying] = React.useState(false);

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  const offers = [
  {
    id: 1,
    title: "AI Brand Commercial",
    price: "Starting at $2,500",
    description: "When first impressions matter, waiting weeks isn't an option. Your brand needs to look premium now—not after months of agency back-and-forth. This is a TV or social media ready cinematic commercial built fast, without cutting corners.",
    bullets: [
    "30–60 second cinematic commercial",
    "Script + visual direction included",
    "One revision round",
    "Delivered in 5–7 days"],

    cta: "Start This Project",
    ctaAction: scrollToContact
  },
  {
    id: 2,
    title: "Launch-Ready Website",
    price: "Starting at $2,500",
    description: "Your website should support revenue, not stall it. You need a site that converts, not a design project that drags on. This gets you live quickly with clear messaging, structure, and polish so you can start collecting leads or closing customers immediately.",
    bullets: [
    "Conversion-focused landing page or small site",
    "Copy, structure, and layout included",
    "Stripe or contact form integration",
    "Delivered in 3–5 days"],

    cta: "Start This Project",
    ctaAction: scrollToContact
  },
  {
    id: 3,
    title: "Brand Accelerator",
    price: "Starting at $4,000",
    description: "Launching piecemeal costs time and momentum. Speed and cohesion matter. This delivers a unified brand presence with a commercial and conversion-ready website that work together—in under two weeks.",
    bullets: [
    "AI Commercial + Website",
    "Full launch presence in under 10 days",
    "Best value for new brands"],

    cta: "Book a Call",
    ctaAction: scrollToContact
  }];


  return (
    <div className="min-h-screen bg-[#0B0D10]">
      {/* SECTION 1 — HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_premium-commercials/artifacts/9m5iq7jv_ChatGPT%20Image%20Jan%2026%2C%202026%2C%2001_13_28%20PM.png"
            alt="Premium creative workspace"
            className="w-full h-full object-cover" />

          {/* Multi-layer Cinematic Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D10] via-[#0B0D10]/85 to-[#0B0D10]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D10] via-transparent to-[#0B0D10]/30" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-[56px] font-semibold text-[#F2F4F7] leading-[1.08] tracking-tight mb-6">
              Fast, High-End Creative — Without Agency Timelines
            </h1>
            <p className="text-base sm:text-lg text-[#A6AFBD] leading-relaxed mb-8 max-w-2xl">
              AI-powered commercials, websites, and digital builds delivered quickly for brands that need momentum now.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={scrollToContact}
                className="group inline-flex items-center gap-3 bg-[#3B82F6] text-white px-7 py-4 text-base font-medium transition-all duration-300 hover:bg-[#2563EB] hover:scale-[1.02] hover:shadow-lg hover:shadow-[#3B82F6]/25">

                Start a Fast-Track Project
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <Link
                to="/#work"
                className="text-[#A6AFBD] hover:text-[#F2F4F7] transition-colors text-base font-medium underline underline-offset-4">

                View Selected Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — FAST-TRACK OFFERS */}
      <section className="py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {offers.map((offer) =>
            <div
              key={offer.id}
              className="bg-[#11151B] border border-white/10 p-8 flex flex-col transition-all duration-300 hover:border-white/20">

                <h3 className="text-xl font-semibold text-[#F2F4F7] mb-2">
                  {offer.title}
                </h3>
                <p className="text-[#3B82F6] font-medium text-lg mb-4">
                  {offer.price}
                </p>
                <p className="text-[#A6AFBD] text-sm leading-relaxed mb-6">
                  {offer.description}
                </p>
                <ul className="space-y-3 mb-8 flex-grow">
                  {offer.bullets.map((bullet, index) =>
                <li key={index} className="text-[#A6AFBD] text-sm flex items-start gap-2">
                      <span className="text-[#3B82F6] mt-1">•</span>
                      {bullet}
                    </li>
                )}
                </ul>
                <button
                onClick={offer.ctaAction}
                className="w-full bg-transparent border border-white/20 text-[#F2F4F7] py-3 text-sm font-medium transition-all duration-300 hover:bg-white/5 hover:border-white/30">

                  {offer.cta}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* SECTION 3 — PROOF */}
      <section className="py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-4 mb-10">
            <h2 className="text-2xl lg:text-3xl font-semibold text-[#F2F4F7]">
              Recent Work
            </h2>
            <div className="flex-1 h-px bg-white/10" aria-hidden="true" />
          </div>

          {/* Card with Video and Screenshot */}
          <div className="border border-white/10 rounded-lg p-6 bg-[#11151B]/50">
            {/* Title */}
            <h3 className="text-xl font-semibold text-[#F2F4F7] mb-4">
              Diamond Club Matchmaking
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Video Embed */}
              <div className="relative aspect-video bg-[#11151B] overflow-hidden rounded">
                {isVideoPlaying ?
                <iframe
                  src="https://www.youtube-nocookie.com/embed/4yFrl_5xk0w?autoplay=1&rel=0&modestbranding=1"
                  title="Diamond Club Matchmaking - High-End Luxury Brand Promo by Ramon Govea"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy" /> :


                <div
                  className="relative w-full h-full cursor-pointer group"
                  onClick={() => setIsVideoPlaying(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setIsVideoPlaying(true)}
                  aria-label="Play Diamond Club Matchmaking video">

                    <img
                    src="https://img.youtube.com/vi/4yFrl_5xk0w/sddefault.jpg"
                    alt="Diamond Club Matchmaking - Luxury matchmaking brand promo by Ramon Govea"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy" />

                    <div className="absolute inset-0 bg-[#0B0D10]/40 flex items-center justify-center transition-opacity duration-300 group-hover:bg-[#0B0D10]/50">
                      <div className="w-16 h-16 rounded-full bg-[#3B82F6]/90 flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110">
                        <Play size={28} className="text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </div>
                }
              </div>

              {/* Website Screenshot */}
              <div className="relative aspect-video bg-[#11151B] overflow-hidden rounded">
                <img
                  src="https://customer-assets.emergentagent.com/job_premium-commercials/artifacts/gi4nf41v_Screenshot%202026-01-26%20at%2012.17.14%20PM.png"
                  alt="Diamond Club Matchmaking website - designed and built by Ramon Govea"
                  className="w-full h-full object-cover object-top"
                  loading="lazy" />

              </div>
            </div>

            {/* Caption */}
            <div className="flex items-start justify-between gap-4 mt-6">
              <p className="!text-sm text-[#A6AFBD]">Luxury matchmaking brand — full site and promotional assets delivered without agency overhead. (Brand Accelerator)

              </p>
              <a
                href="https://diamondclubmatchmaking.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#3B82F6] hover:text-[#60A5FA] transition-colors text-sm font-medium whitespace-nowrap">

                Visit Site
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* King Fox Cleaners Card */}
          <div className="border border-white/10 rounded-lg p-6 bg-[#11151B]/50 mt-6">
            {/* Title */}
            <h3 className="text-xl font-semibold text-[#F2F4F7] mb-4">
              King Fox Cleaners
            </h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Video Embed */}
              <div className="relative aspect-video bg-[#11151B] overflow-hidden rounded">
                {isKingFoxVideoPlaying ? (
                  <iframe
                    src="https://www.youtube-nocookie.com/embed/Su8trRf75uk?autoplay=1&rel=0&modestbranding=1"
                    title="King Fox Cleaners - Premium Local Brand Commercial by Ramon Govea"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="relative w-full h-full cursor-pointer group"
                    onClick={() => setIsKingFoxVideoPlaying(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setIsKingFoxVideoPlaying(true)}
                    aria-label="Play King Fox Cleaners video"
                  >
                    <img
                      src="https://img.youtube.com/vi/Su8trRf75uk/sddefault.jpg"
                      alt="King Fox Cleaners - Premium local brand commercial by Ramon Govea"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-[#0B0D10]/40 flex items-center justify-center transition-opacity duration-300 group-hover:bg-[#0B0D10]/50">
                      <div className="w-16 h-16 rounded-full bg-[#3B82F6]/90 flex items-center justify-center transform transition-transform duration-300 group-hover:scale-110">
                        <Play size={28} className="text-white ml-1" fill="white" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Website Screenshot */}
              <div className="relative aspect-video bg-[#11151B] overflow-hidden rounded">
                <img
                  src="https://customer-assets.emergentagent.com/job_premium-commercials/artifacts/knp8psvc_Screenshot%202026-01-30%20at%208.21.19%20PM.png"
                  alt="King Fox Cleaners website - designed and built by Ramon Govea"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Caption */}
            <div className="flex items-start justify-between gap-4 mt-6">
              <p className="!text-sm text-[#A6AFBD]">
                Premium local brand — commercial and website delivered fast for immediate market presence. (Brand Accelerator)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — APPS & CUSTOM BUILDS */}
      <section className="py-20 lg:py-28 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <h2 className="text-2xl lg:text-3xl font-semibold text-[#F2F4F7] mb-4">
              Need Something More Custom?
            </h2>
            <p className="text-[#A6AFBD] text-base leading-relaxed mb-6">
              I also design and ship MVP Mobile or Browser Apps and Custom Digital Products like eBooks, Visual Mockups, or Product Photo Campaign Assets. These projects are scoped individually and taken on selectively.
            </p>
            <button
              onClick={scrollToContact}
              className="inline-flex items-center gap-2 text-[#3B82F6] hover:text-[#60A5FA] transition-colors text-base font-medium">

              Book a Call to Discuss
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* SECTION 5 — CTA */}
      <section className="relative py-20 lg:py-28 border-t border-white/5 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_premium-commercials/artifacts/2ac7q892_ChatGPT%20Image%20Jan%2026%2C%202026%2C%2001_20_37%20PM.png"
            alt="Premium creative production"
            className="w-full h-full object-cover" />

          {/* Gradient Overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D10] via-[#0B0D10]/85 to-[#0B0D10]/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D10]/80 via-transparent to-[#0B0D10]/40" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl">
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#F2F4F7] mb-4">
              Let&apos;s Move Fast.
            </h2>
            <p className="text-[#A6AFBD] text-base leading-relaxed mb-8">
              If you need something built quickly—and want it to look intentional and expensive—start here.
            </p>
            <button
              onClick={scrollToContact}
              className="group inline-flex items-center gap-3 bg-[#3B82F6] text-white px-7 py-4 text-base font-medium transition-all duration-300 hover:bg-[#2563EB] hover:scale-[1.02] hover:shadow-lg hover:shadow-[#3B82F6]/25">

              Start a Fast-Track Project
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>
    </div>);

};

export default WorkWithMe;